import type { PerspectiveCamera } from 'three'
import { Clock, EventDispatcher, Vector3 } from 'three'
import { debounce } from '@/lib/utils'
import type { CameraViewData } from '@/entities/model3d'
import { tweenCamera } from '@/lib/three-d-editor/tween-camera'
import type { ControlEventMap, SetCameraOptions } from '@/lib/three-d-resources/controls/viewerControls'
import { ControlEvent, DEFAULT_MODEL_TWEEN_DURATION } from '@/lib/three-d-resources/controls/viewerControls'
import type { ViewerControls } from '@/lib/three-d-resources/controls/viewerControls'
import CameraControls from 'camera-controls'
import { compareVec3, roundAbsolute } from '@/lib/compare-camera-value'
import * as THREE from 'three'
import type { CameraUpdateMessage } from '@/composables/useConnectSession/setup-channels'
import type { MouseButtons } from 'camera-controls/dist/types'
import PatchedCameraControls from '@/lib/three-d-editor/patched-camera-controls'

CameraControls.install({ THREE })

class EditControls extends EventDispatcher<ControlEventMap> implements ViewerControls {
  cameraControls: CameraControls
  getCenterHitPoint: () => Vector3 | null = () => null

  private _isTweening: boolean = false
  private cancelTween: () => void = () => {}
  private clock = new Clock()
  private _lastInteraction: MouseButtons[keyof MouseButtons] | null = null

  constructor(private camera: PerspectiveCamera) {
    super()
    this.cameraControls = new PatchedCameraControls(this.camera)
  }

  get isTweening(): boolean {
    return this._isTweening
  }

  set isTweening(newVal: boolean) {
    if (newVal && !this._isTweening) {
      // start tween
      this._isTweening = true
      this.dispatchEvent({ type: ControlEvent.StartTween })
    }
  }

  get target(): [number, number, number] {
    const target = new Vector3()
    this.cameraControls?.getTarget(target)
    return this.cameraControls ? target.toArray() : [0, 0, 0]
  }

  set target([x, y, z]: [number, number, number]) {
    if (!this.cameraControls) return
    this.cameraControls.setTarget(x, y, z)
  }

  onMouseDown = (e: MouseEvent): void => {
    if (!this.cameraControls) return

    if (e.buttons & 1) {
      this._lastInteraction = this.cameraControls.mouseButtons.left
    } else if (e.buttons & 2) {
      this._lastInteraction = this.cameraControls.mouseButtons.right
    } else if (e.buttons & 4) {
      this._lastInteraction = this.cameraControls.mouseButtons.middle
    }

    if (this.cameraControls.enabled && this.isTweening) {
      this.cancelTween()
      this.isTweening = false
    }
  }

  // accessed by event handler, can be swapped out to disable
  private _truckAction: 0 | 2 = CameraControls.ACTION.TRUCK

  enableTruck() {
    this.cameraControls!.dollyToCursor = true
    this.cameraControls!.mouseButtons.middle = CameraControls.ACTION.TRUCK
    this._truckAction = CameraControls.ACTION.TRUCK
  }

  disableTruck() {
    this.cameraControls!.dollyToCursor = false
    this.cameraControls!.mouseButtons.middle = CameraControls.ACTION.NONE
    this._truckAction = CameraControls.ACTION.NONE
  }

  debouncedStopTween = debounce(() => {
    this.dispatchEvent({ type: ControlEvent.StopTween })
  }, 150)

  halt() {
    // Discards any remaining transition
    const { x: px, y: py, z: pz } = this.cameraControls.getPosition(null as any, false)
    const { x: tx, y: ty, z: tz } = this.cameraControls.getTarget(null as any, false)
    this.cameraControls.setLookAt(px, py, pz, tx, ty, tz, false)
  }

  attach(element: HTMLElement): void {
    this.cameraControls.disconnect()
    this.cameraControls.connect(element)
    this.cameraControls.smoothTime = 0.1
    this.cameraControls.maxDistance = 20_000
    this.cameraControls.dollyToCursor = true
    this.cameraControls.dollySpeed = 0.25
    this.cameraControls.minPolarAngle = 0.1 // 6 degrees from vertical to prevent awkward transitions
    this.cameraControls.maxPolarAngle = Math.PI - 0.1 // 174 degrees
    this.cameraControls.mouseButtons.middle = CameraControls.ACTION.TRUCK

    document.addEventListener('keydown', this._keydownHandler)
    document.addEventListener('keyup', this._keyupHandler)
    window.addEventListener('blur', this._documentFocusHandler)

    this.cameraControls.addEventListener('update', () => {
      this.dispatchEvent({ type: ControlEvent.ViewChangedEvent })
    })

    this.cameraControls.addEventListener('control', () => {
      // We need to adjust the target if the controls are panned by the user
      // but not if we're animating for some reason (camera set, pivot set)
      if (!this.isTweening && this.cameraControls) {
        const target = this.cameraControls.getTarget(undefined as any)

        this.dispatchEvent({
          type: ControlEvent.CameraControlEvent,
          payload: { camera: this.camera, target },
        })
      }

      this.debouncedStopTween()
    })

    this.cameraControls.addEventListener('controlstart', () => {
      this.checkIfStuckKeys()
      this.dispatchEvent({ type: ControlEvent.StartTween })
    })

    this.cameraControls.addEventListener('rest', () => {
      if (!this.cameraControls.enabled) return

      // check if the control was not a rotation
      if (this._lastInteraction !== CameraControls.ACTION.ROTATE) {
        const point = this.getCenterHitPoint()
        if (!point) return
        this.cameraControls.setTarget(point.x, point.y, point.z)
      }
      this.debouncedStopTween()
    })

    this.cameraControls.addEventListener('controlend', () => {
      this.debouncedStopTween()
    })

    element.addEventListener('focus', _preventDefault)
    element.addEventListener('mousedown', this.onMouseDown)
  }

  async setCameraData(
    cameraData: CameraViewData,
    { tweenDuration = DEFAULT_MODEL_TWEEN_DURATION, disableControls = true }: SetCameraOptions = {}
  ): Promise<void> {
    const controls = this.cameraControls
    if (!controls) {
      return
    }

    const { camera } = this

    const { position, target } = cameraData
    const fov = cameraData.fov.x

    const tempTarget = new Vector3()
    this.cameraControls?.getTarget(tempTarget)

    const isEquivalentPosition =
      compareVec3(camera.position, position) && compareVec3(tempTarget, target) && roundAbsolute(camera.fov) === roundAbsolute(fov)

    tweenDuration = isEquivalentPosition ? 0.1 : tweenDuration

    this.cancelTween()
    if (tweenDuration) {
      if (disableControls) {
        controls.enabled = false
      }
      this.isTweening = true
      const off = () => {
        if (disableControls) {
          controls.enabled = true
        }
        this.isTweening = false
        this.debouncedStopTween()
      }
      const cancelCamera = tweenCamera(
        camera,
        controls,
        {
          position,
          target,
          fov: { x: fov, y: fov },
        },
        tweenDuration,
        () => {
          this.dispatchEvent({ type: ControlEvent.ViewChangedEvent })
        },
        off
      )
      this.cancelTween = () => {
        cancelCamera()
      }
      return
    }

    const { x: px, y: py, z: pz } = position
    const { x: tx, y: ty, z: tz } = target
    await controls.setLookAt(px, py, pz, tx, ty, tz, false)
    controls.update(this.clock.getDelta())
    camera.fov = fov
    camera.updateProjectionMatrix()
  }

  followControl({ fov, target, position }: CameraUpdateMessage): void {
    const { cameraControls } = this
    if (!cameraControls) {
      return
    }

    const { x: x0, y: y0, z: z0 } = position
    const { x: x1, y: y1, z: z1 } = target
    cameraControls.setLookAt(x0, y0, z0, x1, y1, z1, true)
    this.camera.fov = fov
    this.camera.updateProjectionMatrix()
  }

  update(): boolean {
    const updated = this.cameraControls?.update(this.clock.getDelta())
    return updated ?? false
  }

  detach(): void {
    this.cancelTween()

    // if (this.viewer) {
    //   this.renderManager.renderer.domElement.removeEventListener('mousedown', this.onMouseDown)
    //   element.addEventListener('focus', _preventDefault)
    // }

    this.cameraControls.dispose()

    document.removeEventListener('keydown', this._keydownHandler)
    document.removeEventListener('keyup', this._keyupHandler)
    window.removeEventListener('blur', this._documentFocusHandler)
  }

  setMaxDistance(sceneSize: Vector3) {
    const isZeroPoint = sceneSize.x === 0 && sceneSize.y === 0 && sceneSize.z === 0
    if (!this.cameraControls || isZeroPoint) return

    const largestCoordinate = Math.max(sceneSize.x, sceneSize.y, sceneSize.z)
    this.cameraControls.maxDistance = largestCoordinate * 8
  }

  private checkIfStuckKeys() {
    if (window.event) {
      const metaKey = (window.event as KeyboardEvent).metaKey
      const shiftKey = (window.event as KeyboardEvent).shiftKey
      const ctrlKey = (window.event as KeyboardEvent).ctrlKey

      if (metaKey || shiftKey || ctrlKey) {
        this.cameraControls!.mouseButtons.left = CameraControls.ACTION.TRUCK
        return
      }
    }
    this.cameraControls!.mouseButtons.left = CameraControls.ACTION.ROTATE
  }

  private _buttonsDown = 0
  private _keydownHandler = (event: KeyboardEvent) => {
    if (TruckModifierKeys.hasOwnProperty(event.key)) {
      this._buttonsDown |= TruckModifierKeys[event.key as keyof typeof TruckModifierKeys]
    }
    if (this._buttonsDown > 0) {
      this.cameraControls!.mouseButtons.left = this._truckAction as any
    }
  }
  private _keyupHandler = (event: KeyboardEvent) => {
    if (TruckModifierKeys.hasOwnProperty(event.key)) {
      this._buttonsDown &= ~TruckModifierKeys[event.key as keyof typeof TruckModifierKeys]
    }
    if (this._buttonsDown <= 0) {
      this.cameraControls!.mouseButtons.left = CameraControls.ACTION.ROTATE
    }
  }
  private _documentFocusHandler = () => {
    if (this.cameraControls) {
      this._buttonsDown = 0
      this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE
    }
  }
}

const _preventDefault = (e: Event) => e.preventDefault()

enum TruckModifierKeys {
  Shift = 1 << 0,
  Meta = 1 << 1,
  Control = 1 << 2,
}

export { EditControls, ControlEvent }

let instance: EditControls

export function getEditControls() {
  if (!instance) throw new Error('Init EditControls before getting instance')

  return instance
}

export function initEditControls(camera: PerspectiveCamera) {
  // if (instance) throw new Error('ReadonlyControls already initialized')

  instance = new EditControls(camera)
}
